import type { AccountCategory } from "@fomofi/shared";
import { bucketLabel, type BucketKey } from "@/lib/bucketLabels";

// Mock data for the Dashboard page

/** `unbudgeted` is NOT a verdict — it is the absence of one. A category with no
 *  budget has no ratio to judge, and reporting it as "on track" was #1248's
 *  zero-denominator pass. */
export type BudgetStatus = "on-track" | "approaching" | "over-budget" | "unbudgeted";

// --- New types for redesigned dashboard ---

export interface AllocationCard {
  id: string;
  label: string;
  amount: number;
  percentOfIncome: number;
  icon: string;
  color: string;
  isNew?: boolean;
  /** Per-card basis line, overriding the id-keyed DESCRIPTORS default (BEN-141).
   *  The Dashboard renders TWO rows that both use id "debt" for different
   *  quantities — the monthly SUGGESTED slice and the trailing-12mo ACTUAL debt
   *  outflow — so one descriptor per id could not describe both honestly. */
  descriptor?: string;
}

export interface ForecastPeriod {
  label: string;
  startDate?: string;
  endDate?: string;
  income: number;
  // needs/wants/savings are a true partition of income (sum to income at 50/30/20).
  needs: number;
  wants: number;
  savings: number;
  /** Names the income base this partition rests on — 50/30/20 of PROJECTED period
   *  income. Insights' "Your Plan at a Glance (Per Pay Period)" shows the same three
   *  buckets for the same period off the user's prorated ACTUAL budgets, so the two
   *  cards must each say which they are (BEN-141 4a D5 / 4b 1c). */
  basisLabel?: string;
}

export interface PlanEntry {
  label: string;
  amount: number;
  color: string;
  descriptor: string;
}

export interface SpendingBucket {
  id: string;
  label: string;
  spent: number;
  budgeted: number;
  percentUsed: number;
  color: string;
  /** One line under the figure, for a bucket whose number needs a window to mean
   *  anything. Rollover uses it to name the pay period the money came from
   *  (BEN-141 P31); the others leave it undefined and the card falls back to its
   *  own copy. */
  note?: string;
}

export interface QuickTip {
  message: string;
}

export interface AccountSummary {
  id: string;
  name: string;
  /** Signed for net worth: assets positive, liabilities (credit/loan) negative. */
  balance: number;
  /** The display category, from the server's `account_category` (#719). It is
   *  the shared union rather than a local one so the Accounts card and the Banks
   *  page cannot label the same account differently. */
  type: AccountCategory;
  mask: string;
}

export interface RecentTransaction {
  id: string;
  merchant: string;
  amount: number;
  type: "income" | "expense";
  /** Money in, from the row's direction (FP-055); the sign follows this, not `type`. */
  inflow?: boolean;
  date: string;
  category: string;
  logoUrl?: string;
}

// --- New mock data ---

export const mockAllocationCards: AllocationCard[] = [
  {
    id: "income",
    label: bucketLabel("income"),
    amount: 3369,
    percentOfIncome: 100,
    icon: "DollarSign",
    color: "primary",
  },
  {
    id: "needs",
    label: bucketLabel("needs"),
    amount: 1815,
    percentOfIncome: 53.9,
    icon: "Home",
    color: "info",
  },
  {
    id: "wants",
    label: bucketLabel("wants"),
    amount: 620,
    percentOfIncome: 18.4,
    icon: "ShoppingBag",
    color: "warning",
  },
  {
    id: "savings",
    label: bucketLabel("savings"),
    amount: 400,
    percentOfIncome: 11.9,
    icon: "PiggyBank",
    color: "success",
  },
  {
    id: "splash-fund",
    label: bucketLabel("splash-fund"),
    amount: 724,
    percentOfIncome: 21.4,
    icon: "Sparkles",
    color: "purple",
    isNew: true,
  },
  {
    id: "debt",
    label: bucketLabel("debt"),
    amount: 430,
    percentOfIncome: 12.8,
    icon: "CreditCard",
    color: "destructive",
  },
];

// needs/wants/savings are a 50/30/20 partition that sums exactly to income, so
// the fallback bar height equals the period income (no double-count).
export const mockForecastPeriods: ForecastPeriod[] = [
  { label: "P1\nMay 10-May 23", income: 3369, needs: 1684, wants: 1011, savings: 674 },
  { label: "P2\nMay 24-Jun 6", income: 3369, needs: 1684, wants: 1011, savings: 674 },
  { label: "P3\nJun 7-Jun 20", income: 3369, needs: 1684, wants: 1011, savings: 674 },
  { label: "P4\nJun 21-Jul 4", income: 3369, needs: 1684, wants: 1011, savings: 674 },
  { label: "P5\nJul 5-Jul 18", income: 3369, needs: 1684, wants: 1011, savings: 674 },
  { label: "P6\nJul 19-Aug 1", income: 3369, needs: 1684, wants: 1011, savings: 674 },
];

export const mockSpendingBuckets: SpendingBucket[] = [
  {
    id: "needs",
    label: bucketLabel("needs"),
    spent: 1152,
    budgeted: 1815,
    percentUsed: 63,
    color: "info",
  },
  {
    id: "wants",
    label: bucketLabel("wants"),
    spent: 312,
    budgeted: 620,
    percentUsed: 50,
    color: "warning",
  },
  {
    id: "savings",
    label: bucketLabel("savings"),
    spent: 0,
    budgeted: 400,
    percentUsed: 0,
    color: "success",
  },
  {
    id: "splash-fund",
    label: bucketLabel("splash-fund"),
    spent: 200,
    budgeted: 724,
    percentUsed: 28,
    color: "purple",
  },
  {
    id: "debt",
    label: bucketLabel("debt"),
    spent: 430,
    budgeted: 430,
    percentUsed: 100,
    color: "destructive",
  },
  {
    id: "rollover",
    label: "Rollover (So Far)",
    spent: 120,
    budgeted: 0,
    percentUsed: 100,
    color: "muted-foreground",
  },
];

export const mockAccountSummaries: AccountSummary[] = [
  { id: "checking", name: "Checking", balance: 5430.12, type: "checking", mask: "1234" },
  { id: "savings", name: "Savings", balance: 8210.75, type: "savings", mask: "5678" },
  { id: "credit", name: "Credit Card", balance: -2345.6, type: "credit", mask: "9876" },
  // A retirement account is the row the real data has and the fixture did not,
  // which is how #719 stayed invisible in every mocked screenshot.
  { id: "investment", name: "401(k)", balance: 23631.98, type: "investment", mask: "5555" },
];

export const mockRecentTransactions: RecentTransaction[] = [
  {
    id: "t1",
    merchant: "DoorDash",
    amount: -24.35,
    type: "expense",
    date: "May 12",
    category: "Food & Dining",
    logoUrl: "https://cdn.simpleicons.org/doordash",
  },
  {
    id: "t2",
    merchant: "Starbucks",
    amount: -5.65,
    type: "expense",
    date: "May 10",
    category: "Coffee Shops",
    logoUrl: "https://cdn.simpleicons.org/starbucks",
  },
  {
    id: "t3",
    merchant: "Paycheck",
    amount: 3369.0,
    type: "income",
    date: "May 10",
    category: "Paycheck",
  },
  {
    id: "t4",
    merchant: "Amazon",
    amount: -45.9,
    type: "expense",
    date: "May 9",
    category: "Shopping",
    logoUrl: "https://cdn.simpleicons.org/amazon",
  },
  {
    id: "t5",
    merchant: "Netflix",
    amount: -15.49,
    type: "expense",
    date: "May 8",
    category: "Entertainment",
    logoUrl: "https://cdn.simpleicons.org/netflix",
  },
];

/** One planned-vs-spent pair backing a PayPeriodDonut progress bar. */
export interface PayPeriodBar {
  spent: number;
  planned: number;
  /** FP-037: the debt bar's "no_debts" (no active debt, nothing to plan). */
  reason?: "no_debts" | null;
}

/** Shape of the current-pay-period summary card inputs (PayPeriodDonut).
 *  Nullable fields have no backend source yet (see toPayPeriodInfo) — the card
 *  renders an honest "not tracked yet" state instead of fake zeros. */
export interface PayPeriodInfo {
  currentPeriodLabel: string;
  daysLeft: number;
  /** FP-062: false when no paycheck is detected; the window is then the calendar
   *  month and must not be called a pay period. Absent (mock) reads as detected. */
  detected?: boolean;
  totalPlanned: number;
  totalSpent: number;
  /** FP-176: the part of totalSpent that needs a category. 0 when none. */
  uncategorizedSpent?: number;
  percentUsed: number;
  remaining: number;
  /* `splashFundAvailable` / `splashFundRollover` were here. The first was the
   * period surplus × (wants_pct ÷ 2) — a second figure named Splash Fund on a
   * card that already renders the per-period one in `bars.splash`; the second
   * never had a backend source. Both removed finishing BEN-141 F5. */
  safeToSpend: number | null;
  projectedLeftover: number;
  billsDueBeforeNextPaycheck: number | null;
  /** The label for that figure: it names a paycheck only when there is one (#1251). */
  billsDueLabel: string;
  currentBalance: number | null;
  bars: {
    /** "Bills covered": bills paid of bills due. null = an older backend. */
    needs: PayPeriodBar | null;
    wants: PayPeriodBar;
    splash: PayPeriodBar | null;
    debt: PayPeriodBar | null;
  };
  /** R5: the account's data stopped before this window began. `through` is the
   *  newest transaction (ISO); `reconnect` is state A (a bank needs reconnecting).
   *  Null or absent = current. */
  stale?: { through: string; reconnect: boolean } | null;
}

export const payPeriodInfo: PayPeriodInfo = {
  currentPeriodLabel: "May 10 – May 23",
  daysLeft: 4,
  totalPlanned: 3369,
  totalSpent: 2421,
  percentUsed: 72,
  remaining: 948,
  safeToSpend: 948,
  projectedLeftover: 948,
  billsDueBeforeNextPaycheck: 663,
  billsDueLabel: "Bills due before next paycheck",
  currentBalance: 5240,
  bars: {
    needs: { spent: 1152, planned: 1815 },
    wants: { spent: 312, planned: 620 },
    splash: { spent: 200, planned: 724 },
    debt: { spent: 430, planned: 430 },
  },
};

export interface SpendingCategory {
  id: string;
  name: string;
  allocated: number;
  spent: number;
  status: BudgetStatus;
  children?: SpendingCategory[];
}

export interface AllocationSlice {
  /** Stable bucket key for identity/equality checks (name is display-only). */
  key: BucketKey;
  name: string;
  target: number;
  actual: number;
  color: string;
}

export interface UpcomingBill {
  id: string;
  name: string;
  amount: number;
  dueDate: string; // ISO date string
  category: string;
  isLarge: boolean;
}

export interface AlertCard {
  id: string;
  type: "overage" | "large-bill" | "cash-flow" | "stale-connection";
  title: string;
  message: string;
  severity: "warning" | "destructive" | "info";
}

export interface PayPeriod {
  label: string;
  startDate: string;
  endDate: string;
  totalIncome: number;
  totalSpent: number;
}

// 50/30/20 donut chart data
export const allocationData: AllocationSlice[] = [
  { key: "needs", name: bucketLabel("needs"), target: 50, actual: 54, color: "#6366f1" },
  { key: "wants", name: bucketLabel("wants"), target: 30, actual: 26, color: "#f59e0b" },
  { key: "savings", name: bucketLabel("savings"), target: 20, actual: 20, color: "#10b981" },
];

// Spending progress per top-level category
export const spendingCategories: SpendingCategory[] = [
  {
    id: "needs",
    name: bucketLabel("needs"),
    allocated: 2500,
    spent: 2700,
    status: "over-budget",
    children: [
      { id: "housing", name: "Housing", allocated: 1500, spent: 1500, status: "on-track" },
      { id: "utilities", name: "Utilities", allocated: 300, spent: 320, status: "over-budget" },
      { id: "groceries", name: "Groceries", allocated: 500, spent: 580, status: "over-budget" },
      {
        id: "transport",
        name: "Transportation",
        allocated: 200,
        spent: 300,
        status: "over-budget",
      },
    ],
  },
  {
    id: "wants",
    name: bucketLabel("wants"),
    allocated: 1500,
    spent: 1290,
    status: "on-track",
    children: [
      { id: "dining", name: "Dining Out", allocated: 400, spent: 380, status: "on-track" },
      {
        id: "entertainment",
        name: "Entertainment",
        allocated: 300,
        spent: 310,
        status: "approaching",
      },
      {
        id: "subscriptions",
        name: "Subscriptions",
        allocated: 150,
        spent: 150,
        status: "on-track",
      },
      { id: "shopping", name: "Shopping", allocated: 650, spent: 450, status: "on-track" },
    ],
  },
  {
    id: "savings",
    name: bucketLabel("savings"),
    allocated: 1000,
    spent: 1000,
    status: "on-track",
    children: [
      { id: "emergency", name: "Emergency Fund", allocated: 500, spent: 500, status: "on-track" },
      { id: "retirement", name: "Retirement", allocated: 300, spent: 300, status: "on-track" },
      { id: "investment", name: "Investments", allocated: 200, spent: 200, status: "on-track" },
    ],
  },
];

// Upcoming bills (next 7-14 days from 2026-02-25)
export const upcomingBills: UpcomingBill[] = [
  {
    id: "bill-1",
    name: "Rent",
    amount: 1500,
    dueDate: "2026-03-01",
    category: "Housing",
    isLarge: true,
  },
  {
    id: "bill-2",
    name: "Electric Bill",
    amount: 120,
    dueDate: "2026-03-03",
    category: "Utilities",
    isLarge: false,
  },
  {
    id: "bill-3",
    name: "Car Insurance",
    amount: 185,
    dueDate: "2026-03-05",
    category: "Transportation",
    isLarge: false,
  },
  {
    id: "bill-4",
    name: "Netflix",
    amount: 22.99,
    dueDate: "2026-03-07",
    category: "Subscriptions",
    isLarge: false,
  },
  {
    id: "bill-5",
    name: "Internet",
    amount: 79.99,
    dueDate: "2026-03-10",
    category: "Utilities",
    isLarge: false,
  },
  {
    id: "bill-6",
    name: "Student Loan",
    amount: 350,
    dueDate: "2026-03-12",
    category: "Debt",
    isLarge: true,
  },
];

// Alert cards
export const alertCards: AlertCard[] = [
  {
    id: "alert-1",
    type: "overage",
    title: `${bucketLabel("needs")} budget exceeded`,
    message: `You've overspent your ${bucketLabel(
      "needs",
    )} budget by $200 this period. Groceries and transportation are over.`,
    severity: "destructive",
  },
  {
    id: "alert-2",
    type: "large-bill",
    title: "Large bill due in 4 days",
    message: "Rent of $1,500 is due on March 1st. Make sure you have sufficient funds.",
    severity: "warning",
  },
  {
    id: "alert-3",
    type: "cash-flow",
    title: "Cash flow tight after rent",
    message:
      "After paying rent, you'll have $320 remaining until your next paycheck on March 15th.",
    severity: "warning",
  },
];

// Current pay period
export const currentPayPeriod: PayPeriod = {
  label: "Feb 15 – Mar 15, 2026",
  startDate: "2026-02-15",
  endDate: "2026-03-15",
  totalIncome: 5000,
  totalSpent: 4990,
};

export interface PayPeriodContextSnapshot {
  startDate: string;
  endDate: string;
  daysLeft: number;
  /** FP-062: false when no paycheck is detected. */
  detected?: boolean;
  paydayDate: string;
  /** THE next payday's count (server `next_payday`). */
  payday?: { days: number; is_today: boolean } | null;
  paydayTime?: string;
  needsBudget: number;
  /** CAL-011: the Needs figure is the NEXT period's, because /forecasting/periods/
   *  starts at today, so mid-period its first row is the next one. */
  needsBudgetIsNextPeriod?: boolean;
  billsScheduled: number;
  buffer: number;
  onTrack: boolean;
  paydayFrequencyNote?: string;
}

export const mockPayPeriodContext: PayPeriodContextSnapshot = {
  startDate: "May 1",
  endDate: "May 14",
  daysLeft: 4,
  paydayDate: "May 3",
  paydayTime: "4:10pm",
  needsBudget: 1815,
  billsScheduled: 1780,
  buffer: 35,
  onTrack: true,
  paydayFrequencyNote: "Paydays occur every 2 weeks on Fridays",
};
